import { useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ArrowLeft, ArrowUp, ArrowDown, Buildings, Info } from '@phosphor-icons/react';
import { Tooltip } from 'antd';
import api from '../../../../../../services/api';
import { financeRoutes } from '../../../../../../utils/RoutesApi';
import {
  LoadingMessage,
  MetricCard,
  CardsGrid,
  InfoItem,
  InfoGridETF,
  LogoPlaceholder,
  TickerBadge,
  SummaryCard,
  Header,
  Container,
  Content,
  PeriodFilters,
  CompanyAboutCard,
  CompanyAboutHeader,
  CompanyDescription,
  CompanySiteLink,
  ContentEmpresa,
} from '../StocksDetails/styles.ts';
import {
  GraphicCotationHistoryContainer,
  HeaderTopBar,
  HeaderGraphic,
  FiltersDate,
  FilterSelect,
  StyledQuoteTable,
  PercentValue,
  PercentValueCumulative,
} from '../../../../../../components/ResumoAcao/components/graphics/GraphicCotationHistory/styles';

function formatCurrency(value) {
  if (value === null || value === undefined) return '-';

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 2,
  }).format(value);
}

function formatPercent(value) {
  if (value === null || value === undefined) return '-';
  return `${(value * 100).toFixed(2)}%`;
}

function formatNumber(value) {
  if (value === null || value === undefined) return '-';

  return new Intl.NumberFormat('pt-BR', {
    notation: 'compact',
    maximumFractionDigits: 2,
  }).format(value);
}

function MiniLineChart({ data = [], type = 'number' }) {
  const series = [
    {
      name: 'Valor',
      data: data.map((item) => item.value || 0),
    },
  ];

  const formatTooltipValue = (value) => {
    if (value === null || value === undefined) return '-';

    if (type === 'currency') {
      return formatCurrency(value);
    }

    if (type === 'percent') {
      return formatPercent(value);
    }

    return formatNumber(value);
  };

  const options = {
    chart: {
      type: 'area',
      sparkline: {
        enabled: true,
      },
      toolbar: {
        show: false,
      },
    },
    colors: ['#52C41A'],
    stroke: {
      curve: 'smooth',
      width: 2,
    },
    fill: {
      opacity: 0.15,
      colors: ['#52C41A'],
    },
    tooltip: {
      enabled: true,
      y: {
        formatter: formatTooltipValue,
      },
    },
    dataLabels: {
      enabled: false,
    },
  };

  return (
    <ReactApexChart
      options={options}
      series={series}
      type="area"
      height={55}
    />
  );
}

function HoldingsChart({ holdings = [] }) {
  const topHoldings = holdings.slice(0, 10);

  const values = topHoldings.map((item) =>
    Number(((item.weight || 0) * 100).toFixed(2))
  );

  const total = values.reduce((sum, value) => sum + value, 0);
  const remaining = Math.max(100 - total, 0);

  const options = {
    chart: {
      type: 'donut',
    },
    stroke: {
      show: false,
    },
    legend: {
      show: false,
    },
    plotOptions: {
      pie: {
        startAngle: -90,
        endAngle: 90,
        donut: {
          size: '65%',
          labels: {
            show: false,
          },
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    labels: [
      ...topHoldings.map((item) => item.ticker || item.name || 'Holding'),
      'Outros',
    ],
    colors: [
      '#38486B',
      '#4A5D82',
      '#635185',
      '#7A5290',
      '#99538E',
      '#B9548A',
      '#CD5485',
      '#E66A73',
      '#FFA600',
      '#FFBF47',
      '#E4E5E7',
    ],
    tooltip: {
      y: {
        formatter: (val) => `${val.toFixed(2)}%`,
      },
    },
  };

  return (
    <div style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
      <ReactApexChart
        options={options}
        series={[...values, remaining]}
        type="donut"
        height={180}
        width={220}
      />

      <h6
        style={{
          position: 'absolute',
          top: '50%',
          transform: 'translateY(-50%)',
          fontSize: '1rem',
          fontWeight: 700,
          color: '#111827',
        }}
      >
        {total.toFixed(0)}%
      </h6>
    </div>
  );
}

export default function EtfDetails({ ticker, onBack }) {
    const [asset, setAsset] = useState(null);
    const [kpis, setKpis] = useState([]);
    const [holdingsData, setHoldingsData] = useState(null);
    const [priceHistory, setPriceHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dividendYieldData, setDividendYieldData] = useState(null);
    const [modalChart, setModalChart] = useState({
            isOpen: false,
            title: '',
            value: '',
            data: [],
            period: '5y',
        });
    const dividendYieldSeries = dividendYieldData?.series?.map((item) => ({
      date: item.year,
      value: item.dividends_per_share || 0,
    })) || [];

    const currentDividendYield = dividendYieldData?.current_yield ?? null;

    useEffect(() => {
        async function loadEtfDetails() {
        try {
            setLoading(true);

            const [
              assetResponse,
              holdingsResponse,
              priceResponse,
              kpisResponse,
              dividendYieldResponse,
            ] = await Promise.all([
              api.get(financeRoutes.INTERNATIONAL_ASSET_DETAIL(ticker)),
              api.get(financeRoutes.INTERNATIONAL_ASSET_HOLDINGS(ticker)),
              api.get(financeRoutes.INTERNATIONAL_ASSET_PRICE_HISTORY(ticker), {
                params: { period: '10y' },
              }),
              api.get(financeRoutes.INTERNATIONAL_ASSET_KPIS(ticker)),
              api.get(financeRoutes.INTERNATIONAL_ASSET_DIVIDEND_YIELD(ticker)),
            ]);

            setAsset(assetResponse.data);
            setHoldingsData(holdingsResponse.data);
            setPriceHistory(priceResponse.data || []);
            setKpis(kpisResponse.data || []);
            setDividendYieldData(dividendYieldResponse.data);
        } catch (error) {
            console.error(`Erro ao buscar detalhes do ETF ${ticker}:`, error);
        } finally {
            setLoading(false);
        }
        }

        if (ticker) {
        loadEtfDetails();
        }
    }, [ticker]);

    if (loading) {
        return <LoadingMessage>Carregando detalhes de {ticker}...</LoadingMessage>;
    }

    if (!asset) {
        return <LoadingMessage>Não encontramos detalhes para {ticker}.</LoadingMessage>;
    }

    const latestKpi = asset.latest_kpi || {};
    const holdings = holdingsData?.holdings || [];
    const topHolding = holdings[0];

    const totalMarketValue = holdings.reduce(
        (sum, item) => sum + Number(item.market_value || 0),
        0
    );

    const holdingsCountSeries = holdings.map((item, index) => ({
        date: item.ticker || item.name || `Holding ${index + 1}`,
        value: index + 1,
    }));

    const holdingsWeightSeries = holdings.map((item) => ({
        date: item.ticker || item.name,
        value: Number(((item.weight || 0) * 100).toFixed(2)),
    }));

    const holdingsMarketValueSeries = holdings.map((item) => ({
        date: item.ticker || item.name,
        value: Number(item.market_value || 0),
    }));

    const holdingsSharesSeries = holdings.map((item) => ({
        date: item.ticker || item.name,
        value: Number(item.shares || 0),
    }));

    const totalShares = holdings.reduce(
        (sum, item) => sum + Number(item.shares || 0),
        0
    );

    const lastPrice = priceHistory.length
        ? priceHistory[priceHistory.length - 1]?.value
        : null;

    function filterDataByPeriod(data = [], period = 'max') {
      if (period === 'max') return data;

      const limitDate = new Date();

      const periodMap = {
        '1m': { months: 1 },
        '3m': { months: 3 },
        '6m': { months: 6 },
        '1y': { years: 1 },
        '5y': { years: 5 },
        '10y': { years: 10 },
      };

      const selectedPeriod = periodMap[period];

      if (!selectedPeriod) return data;

      if (selectedPeriod.months) {
        limitDate.setMonth(limitDate.getMonth() - selectedPeriod.months);
      }

      if (selectedPeriod.years) {
        limitDate.setFullYear(limitDate.getFullYear() - selectedPeriod.years);
      }

      return data.filter((item) => {
        if (!item.date) return false;
        return new Date(item.date) >= limitDate;
      });
    }

    function calculateAccumulatedReturns(data = []) {
      const sortedData = [...data]
        .filter((item) => item.date && item.value)
        .sort((a, b) => new Date(a.date) - new Date(b.date));

      const current = sortedData[sortedData.length - 1];

      if (!current) {
        return {
          oneMonth: 0,
          threeMonths: 0,
          sixMonths: 0,
          oneYear: 0,
          fiveYears: 0,
          tenYears: 0,
        };
      }

      const currentDate = new Date(current.date);
      const currentValue = Number(current.value || 0);

      function getReturnByMonths(months) {
        const targetDate = new Date(currentDate);
        targetDate.setMonth(targetDate.getMonth() - months);

        const closestItem = sortedData.find(
          (item) => new Date(item.date) >= targetDate
        );

        if (!closestItem?.value || !currentValue) return 0;

        return ((currentValue - Number(closestItem.value)) / Number(closestItem.value)) * 100;
      }

      return {
        oneMonth: getReturnByMonths(1),
        threeMonths: getReturnByMonths(3),
        sixMonths: getReturnByMonths(6),
        oneYear: getReturnByMonths(12),
        fiveYears: getReturnByMonths(60),
        tenYears: getReturnByMonths(120),
      };
    }

    function formatMarketCap(value) {
      if (value === null || value === undefined || value === '') return '-';

      const number = Number(value);

      if (Number.isNaN(number)) return '-';

      if (number >= 1_000_000_000_000) {
        return `${(number / 1_000_000_000_000).toFixed(2).replace('.', ',')} T`;
      }

      if (number >= 1_000_000_000) {
        return `${(number / 1_000_000_000).toFixed(2).replace('.', ',')} B`;
      }

      if (number >= 1_000_000) {
        return `${(number / 1_000_000).toFixed(2).replace('.', ',')} M`;
      }

      return number.toLocaleString('pt-BR');
    }

    const marketCap =
      latestKpi?.market_cap ??
      asset?.market_cap ??
      asset?.capitalization ??
      asset?.net_assets?.value ??
      null;

    function calculateEtfScore({ holdings = [], asset, priceHistory = [], dividendYieldData }) {
      const top10Weight = holdings
        .slice(0, 10)
        .reduce((sum, item) => sum + Number(item.weight || 0), 0);

      const diversificationScore = Math.min((holdings.length / 50) * 100, 100);
      const concentrationScore = Math.max(100 - top10Weight, 0);

      const netAssets = Number(asset?.net_assets?.value || 0);
      const sizeScore = Math.min((netAssets / 100_000_000_000) * 100, 100);

      const hasDividendHistory = dividendYieldData?.series?.length >= 5;
      const dividendScore = hasDividendHistory ? 80 : 40;

      return (
        diversificationScore * 0.3 +
        concentrationScore * 0.3 +
        sizeScore * 0.25 +
        dividendScore * 0.15
      );
    }

    function EtfHealthChart({ holdings = [], asset, priceHistory = [], dividendYieldData }) {
      const score = calculateEtfScore({
        holdings,
        asset,
        priceHistory,
        dividendYieldData,
      });

      const options = {
        chart: { type: 'donut' },
        labels: ['Saúde do ETF', 'Risco/atenção'],
        colors: ['#52C41A', '#E4E5E7'],
        dataLabels: { enabled: false },
        legend: { show: false },
        plotOptions: {
          pie: {
            donut: {
              size: '70%',
              labels: { show: false },
            },
          },
        },
      };

      return (
        <div style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
          <ReactApexChart
            options={options}
            series={[Number(score.toFixed(0)), 100 - Number(score.toFixed(0))]}
            type="donut"
            height={180}
            width={220}
          />

          <h6
            style={{
              position: 'absolute',
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '1rem',
              fontWeight: 700,
              color: '#111827',
            }}
          >
            {score.toFixed(0)}%
          </h6>
        </div>
      );
    }

    function ChartModal({ modalChart, setModalChart }) {
        const isPriceHistory = modalChart.title === 'Histórico de cotação';
        const isDividendYield = modalChart.title === 'Dividend Yield';

        const filteredData = isDividendYield
          ? modalChart.data || []
          : filterDataByPeriod(modalChart.data || [], modalChart.period);

        const accumulatedReturns = calculateAccumulatedReturns(modalChart.data);
        const latestItem = filteredData[filteredData.length - 1];

        if (!modalChart.isOpen) return null;
        if (modalChart.type === 'holdings') {
          return (
            <div
              onClick={closeModal}
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.55)',
                zIndex: 9999,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                padding: '2rem 1rem',
                overflowY: 'auto',
              }}
            >
              <div
                onClick={(event) => event.stopPropagation()}
                style={{
                  width: 'auto',
                  maxWidth: '90vw',
                  maxHeight: '90vh',
                  background: '#fff',
                  borderRadius: '12px',
                  padding: '2rem',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                }}
              >
                <button
                  type="button"
                  onClick={closeModal}
                  style={{
                    float: 'right',
                    border: 'none',
                    background: 'transparent',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                  }}
                >
                  ×
                </button>

                <h1 style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
                  {modalChart.title}
                </h1>

                <h3 style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                  Quantidade de holdings:{' '}
                  {modalChart.value}
                </h3>

                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '0.75rem', borderBottom: '1px solid #e5e7eb' }}>Rank</th>
                      <th style={{ textAlign: 'left', padding: '0.75rem', borderBottom: '1px solid #e5e7eb' }}>Nome</th>
                      <th style={{ textAlign: 'left', padding: '0.75rem', borderBottom: '1px solid #e5e7eb' }}>Ticker</th>
                      <th style={{ textAlign: 'right', padding: '0.75rem', borderBottom: '1px solid #e5e7eb' }}>Peso</th>
                      <th style={{ textAlign: 'right', padding: '0.75rem', borderBottom: '1px solid #e5e7eb' }}>Valor de Mercado</th>
                      <th style={{ textAlign: 'right', padding: '0.75rem', borderBottom: '1px solid #e5e7eb' }}>Cotas/Ações</th>
                    </tr>
                  </thead>

                  <tbody>
                    {modalChart.data?.map((holding) => (
                      <tr key={holding.id || `${holding.rank}-${holding.name}`}>
                        <td style={{ padding: '0.75rem', borderBottom: '1px solid #f3f4f6' }}>
                          {holding.rank || '-'}
                        </td>

                        <td style={{ padding: '0.75rem', borderBottom: '1px solid #f3f4f6' }}>
                          {holding.name || '-'}
                        </td>

                        <td style={{ padding: '0.75rem', borderBottom: '1px solid #f3f4f6' }}>
                          {holding.ticker || '-'}
                        </td>

                        <td style={{ padding: '0.75rem', borderBottom: '1px solid #f3f4f6', textAlign: 'right' }}>
                          {formatPercent(Number(holding.weight || 0) / 100)}
                        </td>

                        <td style={{ padding: '0.75rem', borderBottom: '1px solid #f3f4f6', textAlign: 'right' }}>
                          {formatCurrency(holding.market_value)}
                        </td>

                        <td style={{ padding: '0.75rem', borderBottom: '1px solid #f3f4f6', textAlign: 'right' }}>
                          {formatNumber(holding.shares)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        }


        const series = [
            {
            name: modalChart.title,
            data: filteredData.map((item) => item.value || 0),
            },
        ];

        const options = {
            chart: {
            type: 'area',
            toolbar: {
                show: true,
            },
            },
            xaxis: {
            categories: filteredData.map((item) => item.date),
            },
            stroke: {
            curve: 'smooth',
            width: 2,
            },
            fill: {
            opacity: 0.25,
            },
            dataLabels: {
            enabled: false,
            },
        };

        function closeModal() {
            setModalChart({
            isOpen: false,
            title: '',
            value: '',
            data: [],
            period: '5y',
            });
        }

        return (
          <div
            onClick={closeModal}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.55)',
              zIndex: 9999,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              padding: '2rem 1rem',
              overflowY: 'auto',
            }}
            >
            <div
                onClick={(event) => event.stopPropagation()}
                style={{
                  width: '80%',
                  maxWidth: '90vw',
                  maxHeight: '90vh',
                  background: '#fff',
                  borderRadius: '12px',
                  padding: '2rem',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                }}
            >
                <button
                type="button"
                onClick={closeModal}
                style={{
                    float: 'right',
                    border: 'none',
                    background: 'transparent',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                }}
                >
                ×
                </button>

                <h2 style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                  {isPriceHistory ? 'Cotação histórica' : modalChart.title}
                </h2>

                {isPriceHistory ? (
                  <GraphicCotationHistoryContainer>
                    <HeaderTopBar>
                      <HeaderGraphic>
                        <div className="fechamento">
                          <p>
                            Fechamento de{' '}
                            <b>
                              {latestItem?.date
                                ? new Date(latestItem.date).toLocaleDateString('pt-BR')
                                : '-'}
                            </b>
                          </p>

                          <h1>
                            {latestItem?.value ? formatCurrency(latestItem.value) : '-'}
                          </h1>

                          <PercentValue upValue={accumulatedReturns.sixMonths > 0}>
                            <p className="text-variation">
                              *Variação
                              <br />
                              da <b style={{ borderBottom: 'none' }}>Cotação</b>
                            </p>

                            {accumulatedReturns.sixMonths > 0 ? <ArrowUp /> : <ArrowDown />}

                            {accumulatedReturns.sixMonths.toFixed(2)}%
                          </PercentValue>
                        </div>

                        <FiltersDate>
                          {[
                            { label: '1 Mês', value: '1m' },
                            { label: '3 Meses', value: '3m' },
                            { label: '6 Meses', value: '6m' },
                            { label: '1 Ano', value: '1y' },
                            { label: '5 Anos', value: '5y' },
                            { label: '10 Anos', value: '10y' },
                            { label: 'Máx', value: 'max' },
                          ].map((period) => (
                            <FilterSelect
                              key={period.value}
                              type="button"
                              isSelected={modalChart.period === period.value}
                              onClick={() =>
                                setModalChart((prev) => ({
                                  ...prev,
                                  period: period.value,
                                }))
                              }
                            >
                              {period.label}
                            </FilterSelect>
                          ))}
                        </FiltersDate>
                      </HeaderGraphic>

                      <StyledQuoteTable>
                        <table>
                          <thead>
                            <tr>
                              <th>Percentual acumulado</th>
                              <th>Período</th>
                            </tr>
                          </thead>

                          <tbody>
                            {[
                              ['1 mês', accumulatedReturns.oneMonth],
                              ['3 meses', accumulatedReturns.threeMonths],
                              ['6 meses', accumulatedReturns.sixMonths],
                              ['12 meses', accumulatedReturns.oneYear],
                              ['5 anos', accumulatedReturns.fiveYears],
                              ['10 anos', accumulatedReturns.tenYears],
                            ].map(([label, value]) => (
                              <tr key={label}>
                                <td>
                                  <PercentValueCumulative upValue={value > 0}>
                                    {value > 0 ? <ArrowUp /> : <ArrowDown />}
                                    {value.toFixed(2)}%
                                  </PercentValueCumulative>
                                </td>

                                <td>{label}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </StyledQuoteTable>
                    </HeaderTopBar>
                  </GraphicCotationHistoryContainer>
                ) : (
                  <h1 style={{ textAlign: 'center', marginBottom: '2rem' }}>
                    {modalChart.value}
                  </h1>
                )}

                {!isPriceHistory && !isDividendYield && (
                  <PeriodFilters>
                    {[
                      { label: '1 Mês', value: '1m' },
                      { label: '3 Meses', value: '3m' },
                      { label: '6 Meses', value: '6m' },
                      { label: '1 Ano', value: '1y' },
                      { label: '5 Anos', value: '5y' },
                      { label: '10 Anos', value: '10y' },
                      { label: 'Máx', value: 'max' },
                    ].map((period) => (
                      <button
                        key={period.value}
                        type="button"
                        className={modalChart.period === period.value ? 'active' : ''}
                        onClick={() =>
                          setModalChart((prev) => ({
                            ...prev,
                            period: period.value,
                          }))
                        }
                      >
                        {period.label}
                      </button>
                    ))}
                  </PeriodFilters>
                )}

                <ReactApexChart
                options={options}
                series={series}
                type="area"
                height={320}
                />
            </div>
            </div>
        );
    }

  return (
    <Container>
      <Content>
        <Header>
          <button type="button" onClick={onBack}>
            <ArrowLeft size={20} />
            {asset.name
              ?.split(' ')
              .slice(0, 2)
              .join(' ')}
          </button>
        </Header>

        <SummaryCard>
          <div>
            <Tooltip title={`${formatCurrency(lastPrice)}`}>
              <TickerBadge>{asset.ticker}</TickerBadge>
            </Tooltip>

            <LogoPlaceholder>
              <Buildings size={56} weight="fill" />
            </LogoPlaceholder>
          </div>

          <InfoGridETF>
            <InfoItem>
              <span>Nome da Empresa:</span>
              <strong>{asset.name || 'não informado'}</strong>
            </InfoItem>

            <InfoItem>
              <span>Ticker:</span>
              <strong>{asset.ticker || 'não informado'}</strong>
            </InfoItem>

            <InfoItem>
              <span>Capitalização:</span>
              <strong>{formatMarketCap(marketCap)}</strong>
            </InfoItem>

            <InfoItem>
              <span>Tipo de ativo:</span>
              <strong>{asset.asset_type || 'não informado'}</strong>
            </InfoItem>

            <InfoItem>
              <span>Setor:</span>
              <strong>Fundo de índice</strong>
            </InfoItem>

            <InfoItem>
              <span>Moeda:</span>
              <strong>{asset.currency || 'não informado'}</strong>
            </InfoItem>
          </InfoGridETF>
          <div
            style={{
              position: 'absolute',
              right: '40px',
              top: '120px',
              opacity: 0.08,
              fontSize: '6rem',
              fontWeight: 900,
              color: '#EC6643',
              transform: 'rotate(45deg)',
            }}
          >
            ETF
          </div>
          {/* <EtfHealthChart
            holdings={holdings}
            asset={asset}
            priceHistory={priceHistory}
            dividendYieldData={dividendYieldData}
          /> */}
        </SummaryCard>
      </Content>

      <CardsGrid>
        <MetricCard
          onClick={() =>
            setModalChart({
              isOpen: true,
              title: 'Participações',
              subtitle: 'Composição da carteira do ETF',
              value: formatNumber(holdings.length),
              data: holdings,
              type: 'holdings',
              period: 'max',
            })
          }
          style={{
            background:
              'linear-gradient(135deg, #ffffff 0%, #ffffff 100%)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* <div
            style={{
              position: 'absolute',
              right: '97px',
              top: '10px',
              opacity: 0.08,
              fontSize: '6rem',
              fontWeight: 900,
              color: '#EC6643',
            }}
          >
            ETF
          </div> */}

          <header>
            Participações
            <Tooltip title="Número de ativos na carteira do ETF">
              <Info size={14} color="#ec6643" />
            </Tooltip>
          </header>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              marginTop: '0.5rem',
            }}
          >
            <div>
              <h1
                style={{
                  margin: 0,
                  fontSize: '2.6rem',
                  lineHeight: 1,
                  color: '#111827',
                }}
              >
                {holdings.length}
              </h1>

              <span
                style={{
                  fontSize: '0.8rem',
                  color: '#6b7280',
                  fontWeight: 500,
                }}
              >
                ativos na carteira
              </span>
            </div>

            <div
              style={{
                height: '52px',
                width: '1px',
                background: '#e5e7eb',
              }}
            />

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.25rem',
                fontSize: '0.8rem',
              }}
            >
              {holdings.slice(0, 3).map((holding) => (
                <div
                  key={holding.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.35rem',
                  }}
                >
                  <div
                    style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '999px',
                      background: '#EC6643',
                    }}
                  />

                  <span
                    style={{
                      color: '#374151',
                      fontWeight: 600,
                    }}
                  >
                    {holding.ticker ||
                      holding.name
                        ?.split(' ')
                        .slice(0, 1)
                        .join(' ')}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div
            style={{
              marginTop: '1rem',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.35rem',
              background: '#fff3ee',
              color: '#EC6643',
              borderRadius: '999px',
              padding: '0.35rem 0.75rem',
              fontSize: '0.75rem',
              fontWeight: 700,
            }}
          >
            Top 10 representam {holdings
              .slice(0, 10)
              .reduce((sum, item) => sum + Number(item.weight || 0), 0)
              .toFixed(2)}
            %
          </div>
        </MetricCard>

        <MetricCard
          onClick={() =>
            setModalChart({
              isOpen: true,
              title: 'Dividend Yield',
              value: formatPercent(currentDividendYield || 0),
              data: dividendYieldSeries,
              period: 'max',
            })
          }
        >
          <header>
            Dividend Yield
            <Tooltip title="Fluxo de caixa livre em relação ao valor de mercado">
              <Info size={14} color="#ec6643" />
            </Tooltip>
          </header>
          <h3>{formatPercent(currentDividendYield || 0)}</h3>
          <MiniLineChart data={dividendYieldSeries} type="percent" />
        </MetricCard>

        <MetricCard
            onClick={() =>
              setModalChart({
                isOpen: true,
                title: 'Histórico de cotação',
                value: formatCurrency(lastPrice),
                data: priceHistory,
                period: '6m',
              })
            }
            >
          <header>
            Histórico de cotação
            <Tooltip title="Histórico de preço do ETF">
              <Info size={14} color="#ec6643" />
            </Tooltip>
          </header>
          <h3>{formatCurrency(lastPrice)}</h3>
          <MiniLineChart data={priceHistory} type="currency" />
        </MetricCard>

      </CardsGrid>
      <ContentEmpresa>
        <CompanyAboutCard>
          <CompanyAboutHeader>
            <div>
              <span>Sobre o ETF</span>
              <h2>{asset.name || '-'}</h2>
            </div>

            {asset.website && (
              <CompanySiteLink
                href={asset.website}
                target="_blank"
                rel="noopener noreferrer"
              >
                Acessar site da empresa
              </CompanySiteLink>
            )}
          </CompanyAboutHeader>

          <CompanyDescription>
            {asset.description ||
              'Ainda não temos uma descrição cadastrada para esta empresa.'}
          </CompanyDescription>

        </CompanyAboutCard>
      </ContentEmpresa>
      <ChartModal modalChart={modalChart} setModalChart={setModalChart} />
      
    </Container>
  );
}